import { createStore, createStyleSet } from 'botframework-webchat';
import classNames from 'classnames';
import React, { memo, useCallback, useMemo, useState } from 'react';
import { Middleware } from 'redux';
import WebChat from './WebChat';
import { appConfig } from '../../../utils/app.config';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';

// import './fabric-icons-inline.css';
import './MinimizableWebChat.css';

function MinimizableWebChat({ bot = "B0002" }) {
  const [loaded, setLoaded] = useState(true);
  const [minimized, setMinimized] = useState(false);
  const [newMessage, setNewMessage] = useState(false);
  const [side, setSide] = useState('right');
  const [token, setToken] = useState<string>('');
  const [isExpanded, setIsExpanded] = useState(true);
  const [sendMessage, setSendMessage] = useState(false); // Track voice state
  const [message, setMessage] = useState('');
  const [isListening, setIsListening] = useState(false); // Track if listening to speech

  // Middleware for Redux store
  const middleware: Middleware = ({ dispatch }) => (next) => (action: any) => {
    if (action.type === 'DIRECT_LINE/CONNECT_FULFILLED') {
      dispatch({
        type: 'WEB_CHAT/SEND_EVENT',
        payload: {
          name: 'webchat/join',
          value: {
            language: window.navigator.language
          }
        }
      });
    } else if (action.type === 'DIRECT_LINE/INCOMING_ACTIVITY') {
      if (action.payload.activity.from.role === 'bot') {
        setNewMessage(true);
      }
    }

    return next(action);
  };

  const store = useMemo(() => createStore({}, middleware), []);

  const styleSet = useMemo(() => createStyleSet({
    backgroundColor: 'Transparent'
  }), []);

  //   styleSet.textContent = {
  //     ...styleSet.textContent,
  //     fontFamily: "'Comic Sans MS', 'Arial', sans-serif",
  //     fontWeight: 'bold'
  //  };
  const styleOptions = useMemo(() => (
    {

      backgroundColor: 'Transparent',
      botAvatarImage: './static/img/logos/bot-logo-05.svg',
      botAvatarBackgroundColor: 'rgba(0, 0,255, .1)',
      botAvatarInitials: 'BF',
      bubbleNubSize: 10,
      bubbleBorderRadius: 10,
      bubbleNubOffset: 6,
      bubbleBackground: 'rgba(0, 0, 255, .1)',
      bubbleFromUserBackground: 'rgba(0, 0,255, .1)',
      userAvatarImage: './static/img/logos/bot-logo-05.svg',
      userAvatarInitials: 'WC',
      userAvatarBackgroundColor: '#F77',
      bubbleFromUserBorderRadius: 10,
      bubbleFromUserNubOffset: 6,
      bubbleFromUserNubSize: 6,
      avatarSize: 40,
      emojiSet: {
        ':sheep:': '🐑',
        '<3': '❤️'
      },
      hideUploadButton: false, // Enable upload button
      uploadButtonColor: '#0078D4',
      sendBoxBackground: 'white',
      sendBoxTextColor: 'black'
    }
  ), []);
  const handleFetchToken = useCallback(async () => {
    const authToken = sessionStorage.getItem('cva.jwt.access.token');
    const requestBody = {
      "user": {
        "id": "vijay.panwar@rr.com",
        "name": "Vijay Panwar"
      },
      "trustedOrigins": [
        "https://aabbb.com"
      ]
    };
    if (!token) {
      const res = await fetch(
        `${appConfig.baseUrl}/api/v2.0/litebot/tokens/generate?botId=${bot}&domainType=cvadirectline`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${authToken}`, // Add the Authorization header
            'Content-Type': 'application/json'      // Optional: Set content type to JSON
          },
          body: JSON.stringify(requestBody)
        }
      );
      const { token } = await res.json();
      console.log("----------token:", token)
      setToken(token);
    }
  }, [setToken, token]);

  const handleMaximizeButtonClick = useCallback(() => {
    setLoaded(true);
    setMinimized(false);
    setNewMessage(false);
  }, []);

  const handleMinimizeButtonClick = useCallback(() => {
    setMinimized(true);
    setNewMessage(false);
  }, []);

  const handleExpandButtonClick = useCallback(() => {
    setIsExpanded(!isExpanded); // Toggle expansion
  }, [isExpanded]);

  const handleSwitchButtonClick = useCallback(() => {
    setSide(side === 'left' ? 'right' : 'left');
  }, [side]);

  // Voice recognition
  const handleVoiceButtonClick = useCallback(() => {
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  }, [isListening]);

  const startListening = () => {
    setIsListening(true);
    // SpeechRecognition setup
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.lang = 'en-US';

    recognition.onresult = (event: { results: { [x: string]: { transcript: any; }[]; }; resultIndex: string | number; }) => {
      const transcript = event.results[event.resultIndex][0].transcript;
      console.log("Recognized Speech: ", transcript);
      sendToBot(transcript); // Send speech as text to bot
    };

    recognition.onend = () => {
      setIsListening(false); // Reset listening state
    };

    recognition.start();
  };

  const stopListening = () => {
    setIsListening(false);
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (SpeechRecognition) {
      const recognition = new SpeechRecognition();
      recognition.stop();
    }
  };

  const sendToBot = (message: any) => {
    setMessage(message);
    setSendMessage(true);
    handleExpandButtonClick()
  };

  return (
    <div className="minimizable-web-chat">
      {loaded && (
        <div
          className={classNames(
            side === 'left' ? 'chat-box left' : 'chat-box right',
            minimized ? 'hide' : '',
            isExpanded ? 'expanded' : 'collapse' // Apply expanded class when expanded
          )}
        >

          <header>
            <div className="chat-brand-logo">
              <img src={appConfig.imagesUrl + "/logos/bot-logo-md.png"} alt="Company Logo" />
              <div className="chat-brand-txt"><span>CVA</span></div>
            </div>

            <div className="filler" />
            <button className="expand" onClick={handleExpandButtonClick} type="button">
              <i className={!isExpanded ? 'bi-arrows-angle-expand' : 'bi-arrows-angle-contract'} ></i>
            </button>
          </header>
          <WebChat
            className="react-web-chat"
            onFetchToken={handleFetchToken}
            store={store}
            styleOptions={styleOptions}
            styleSet={styleSet}
            token={token}
            sendMessage={sendMessage}
            setSendMessage={(value) => { setSendMessage(value) }}
            message={message}
          />
        </div>
      )}
    </div>
  );
}

export default memo(MinimizableWebChat);
